/**
 * Coordinate and Unit Conversion Utilities
 *
 * This module provides centralized conversion functions for the TestAreasVisualizer system.
 * It handles the complex conversions between feet/meters and geographic coordinates (degrees)
 * that are needed for accurate plot placement and visualization on maps.
 *
 * Key responsibilities:
 * - Convert between imperial (feet) and metric (meters) units
 * - Convert between meters and geographic degrees for map positioning
 * - Provide consolidated plot dimension calculations
 * - Calculate maximum field dimensions from bounding boxes
 * - Handle geospatial buffer tolerances consistently
 *
 * Used by: plotValidation.ts, plotVisualization.ts, visualizationBuilder.ts, TestAreasVisualizer.tsx
 */

// Constants for unit conversions
export const FEET_TO_METERS = 0.3048;
export const METERS_TO_FEET = 3.28084;
export const METERS_TO_DEGREES_LNG = 1 / 111320; // Approximate conversion
export const METERS_TO_DEGREES_LAT = 1 / 110540; // Approximate conversion

// Buffer tolerance for geospatial calculations (meters)
export const SLOP_METERS = 3;

// Convert feet to meters
export function feetToMeters(feet: number): number {
  return feet * FEET_TO_METERS;
}

// Convert meters to feet
export function metersToFeet(meters: number): number {
  return meters * METERS_TO_FEET;
}

// Convert meters to degrees (longitude)
export function metersToDegreesLng(meters: number): number {
  return meters * METERS_TO_DEGREES_LNG;
}

// Convert meters to degrees (latitude)
export function metersToDegreesLat(meters: number): number {
  return meters * METERS_TO_DEGREES_LAT;
}

// Convert degrees to meters (longitude)
export function degreesToMetersLng(degrees: number): number {
  return degrees / METERS_TO_DEGREES_LNG;
}

// Convert degrees to meters (latitude)
export function degreesToMetersLat(degrees: number): number {
  return degrees / METERS_TO_DEGREES_LAT;
}

// Comprehensive plot dimension conversion
export interface PlotDimensions {
  widthFt: number;
  lengthFt: number;
  widthM: number;
  lengthM: number;
  widthDeg: number;
  lengthDeg: number;
}

export function convertPlotDimensions(
  plotWidthFt: number,
  plotLengthFt: number,
): PlotDimensions {
  const widthM = feetToMeters(plotWidthFt);
  const lengthM = feetToMeters(plotLengthFt);

  return {
    widthFt: plotWidthFt,
    lengthFt: plotLengthFt,
    widthM,
    lengthM,
    widthDeg: metersToDegreesLng(widthM),
    lengthDeg: metersToDegreesLat(lengthM),
  };
}

// Buffer tolerance conversion
export function getSlopInDegrees(): { lng: number; lat: number } {
  return {
    lng: metersToDegreesLng(SLOP_METERS),
    lat: metersToDegreesLat(SLOP_METERS),
  };
}

// Convert plot buffer dimensions from feet to meters
export function convertBufferDimensions(
  plotBufferWidthFt: number,
  plotBufferLengthFt: number,
): { widthM: number; lengthM: number } {
  return {
    widthM: feetToMeters(plotBufferWidthFt),
    lengthM: feetToMeters(plotBufferLengthFt),
  };
}

// Calculate maximum possible dimensions from bounding box
export function calculateMaxDimensionsFromBounds(
  bounds: [number, number, number, number], // [minLng, minLat, maxLng, maxLat]
  safetyFactor: number = 0.8,
): { maxWidthFt: number; maxLengthFt: number } {
  const [minLng, minLat, maxLng, maxLat] = bounds;
  const slop = getSlopInDegrees();

  const maxWidthFt = Math.floor(
    degreesToMetersLng(maxLng - minLng - 2 * slop.lng) *
      METERS_TO_FEET *
      safetyFactor,
  );

  const maxLengthFt = Math.floor(
    degreesToMetersLat(maxLat - minLat - 2 * slop.lat) *
      METERS_TO_FEET *
      safetyFactor,
  );

  return { maxWidthFt, maxLengthFt };
}
